import React, { useMemo, useState } from 'react';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import { Box, Button, Checkbox, FormControlLabel, FormHelperText, Stack, Typography } from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import appConfig from '../../../config/appConfig';
import OptionalExternalLink from '../../../components/OptionalExternalLink';
import { useSignupReferral } from '../hooks/useSignupReferral';
import SignupBrandLogos from './SignupBrandLogos';

export const createSignupEmailSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().required(t.name_required),
    email: Yup.string().email(t.invalid_email_format).required(t.email_required),
    organization_name: Yup.string().required(t.organization_name_required),
    phone_number: Yup.string().required(t.phone_number_required),
    contact_email: Yup.string().email(t.invalid_contact_email_format).required(t.contact_email_required),
    termsAndCondition: Yup.boolean().oneOf([true], t.accept_terms_required),
  });

const SignupEmailForm = ({ initialEmail }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signupPath, referredBy } = useSignupReferral();
  const { users: u, language } = useTranslatedMessages();
  const t = u.signup_email ?? {};
  const v = t.validations ?? {};

  const schema = useMemo(
    () =>
      createSignupEmailSchema({
        name_required: v.name_required ?? 'Name is required',
        invalid_email_format: v.invalid_email_format ?? 'Invalid email format',
        email_required: v.email_required ?? 'Email is required',
        organization_name_required: v.organization_name_required ?? 'Organization name is required',
        phone_number_required: v.phone_number_required ?? 'Phone number is required',
        invalid_contact_email_format: v.invalid_contact_email_format ?? 'Invalid contact email format',
        contact_email_required: v.contact_email_required ?? 'Contact email is required',
        accept_terms_required: v.accept_terms_required ?? 'You must accept the terms and conditions',
      }),
    [v],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      email: initialEmail || '',
      termsAndCondition: false,
    },
    resolver: yupResolver(schema),
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/signup', {
        ...data,
        ...(referredBy ? { referred_by: referredBy } : {}),
      })
      .then((res) => {
        dispatch(
          showSnackbar({
            message:
              res.data.message ||
              t.snackbar?.signup_thank_you ||
              'Thank you for signing up! Our team will get back to you shortly.',
            severity: 'success',
          }),
        );
        // Navigate to waiting page with email and timestamp
        navigate(signupPath, {
          replace: true,
          state: {
            stage: 'waiting',
            email: data.email,
            submittedAt: res.data.submittedAt || new Date().toISOString(),
          },
        });
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.errors?.message ?? t.snackbar?.generic_error ?? 'Something went wrong.',
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <SignupBrandLogos />
      <Stack spacing={2}>
        <CustomTextInput
          name="email"
          label={t.fields?.account_email ?? 'Account Email'}
          control={control}
          errors={errors}
        />
        <CustomTextInput name="name" label={t.fields?.full_name ?? 'Full Name'} control={control} errors={errors} />
        <CustomTextInput
          name="organization_name"
          label={t.fields?.organization_name ?? 'Organization Name'}
          control={control}
          errors={errors}
        />
        <CustomTextInput
          name="phone_number"
          label={t.fields?.phone_number ?? 'Phone Number'}
          control={control}
          errors={errors}
        />
        <CustomTextInput
          name="contact_email"
          label={t.fields?.contact_email ?? 'Contact Email'}
          control={control}
          errors={errors}
        />
        <Controller
          name="termsAndCondition"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  color="primary"
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                />
              }
              label={
                <Box>
                  {t.terms?.prefix ?? 'I agree to the'}{' '}
                  <OptionalExternalLink href={appConfig.termsUrl}>
                    {t.terms?.terms_and_conditions ?? 'terms & conditions'}
                  </OptionalExternalLink>{' '}
                  {t.terms?.and ?? 'and'}{' '}
                  <OptionalExternalLink href={appConfig.privacyUrl}>
                    {t.terms?.privacy_policy ?? 'privacy policy'}
                  </OptionalExternalLink>{' '}
                  {(t.terms?.suffix ?? 'of {brand} platform.').replace('{brand}', appConfig.brandName)}
                </Box>
              }
            />
          )}
        />
        {errors.termsAndCondition && (
          <FormHelperText error>{get(errors, 'termsAndCondition.message', '')}</FormHelperText>
        )}
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)}>
          {loading
            ? (t.actions?.requesting ?? 'Requesting...')
            : (t.actions?.request_activation ?? 'Request Account Activation')}
        </Button>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t.links?.already_have_account ?? 'Already have an account?'}{' '}
          <Link to="/login" style={{ textDecoration: 'none', fontWeight: 400 }}>
            {t.links?.sign_in ?? 'Sign In'}
          </Link>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t.links?.facing_issues ?? 'Facing Issues?'}{' '}
          <a href={`mailto:${appConfig.brandEmail}`} style={{ textDecoration: 'none', fontWeight: 400 }}>
            {t.links?.contact_support ?? 'Contact Support'}
          </a>
        </Typography>
      </Stack>
    </>
  );
};

export default SignupEmailForm;
